/**
 * ai-eval-tab.tsx — Tab "Kiểm định": bộ câu hỏi vàng + lịch sử chạy.
 *
 * Vì sao tồn tại: sửa tài liệu logic / kịch bản / model xong, không ai biết các
 * câu cũ còn được trả lời đúng không. Tab này giữ vài chục câu hỏi thật kèm
 * tiêu chí chấm; một nút chạy toàn bộ qua đường MÔ PHỎNG (không gửi gì ra
 * khách) rồi LLM chấm đạt/trượt từng câu.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { FlaskConical, Loader2, Pencil, Play, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch, Textarea } from '@/components/ui/misc'
import { EmptyState, ErrorState, Loading } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { useAiBots } from '@/hooks/use-ai'
import {
  EVAL_STATUS_LABELS, EVAL_VERDICT_LABELS, evalVerdictVariant,
  useCreateEvalCase, useDeleteEvalCase, useEvalCases, useEvalRunDetail, useEvalRuns,
  useStartEvalRun, useUpdateEvalCase,
  type EvalCase, type EvalRun,
} from '@/hooks/use-ai-eval'

const NO_BOT = '__none__'

function fmtTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime())
    ? '—'
    : `${d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' })}`
}

export function AiEvalTab({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('aiOps')
  const casesQ = useEvalCases()
  const runsQ = useEvalRuns()
  const start = useStartEvalRun()
  const del = useDeleteEvalCase()

  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<EvalCase | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)

  const cases = casesQ.data?.cases ?? []
  const runs = runsQ.data?.runs ?? []
  const running = runs.some((r) => r.status === 'running')
  const enabledCount = cases.filter((c) => c.enabled).length

  const handleRun = () => {
    start.mutate(
      {},
      {
        onSuccess: () => toast.success(t('eval.started')),
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            {t('eval.intro')}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('eval.cost')}
          </p>
        </div>
        {canEdit && (
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" onClick={() => { setEditing(null); setEditorOpen(true) }}>
              <Plus /> {t('eval.addCase')}
            </Button>
            <Button onClick={handleRun} disabled={running || start.isPending || enabledCount === 0}>
              {running ? <Loader2 className="animate-spin" /> : <Play />}
              {running ? t('eval.running') : t('eval.runAll', { count: enabledCount })}
            </Button>
          </div>
        )}
      </div>

      {/* ── Danh sách câu hỏi ── */}
      {casesQ.isLoading ? (
        <Loading />
      ) : casesQ.isError ? (
        <ErrorState message={apiError(casesQ.error)} />
      ) : cases.length === 0 ? (
        <EmptyState
          icon={FlaskConical}
          title={t('eval.emptyTitle')}
          description={t('eval.emptyDesc')}
        />
      ) : (
        <div className="space-y-2">
          {cases.map((c) => (
            <div key={c.id} className="flex items-start gap-3 rounded-lg border p-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{c.name}</span>
                  {!c.enabled && <Badge variant="secondary" className="text-[10px]">{t('eval.paused')}</Badge>}
                  {c.botId && <Badge variant="outline" className="text-[10px]">{t('eval.ownAgent')}</Badge>}
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground" title={c.question}>
                  {t('eval.asked', { text: c.question })}
                </p>
                <p className="truncate text-xs text-muted-foreground" title={c.criteria}>
                  {t('eval.graded', { text: c.criteria })}
                </p>
              </div>
              {canEdit && (
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost" size="icon" className="h-7 w-7"
                    aria-label={t('eval.editCase')}
                    onClick={() => { setEditing(c); setEditorOpen(true) }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                    aria-label={t('eval.deleteCase')}
                    onClick={() =>
                      del.mutate(c.id, {
                        onSuccess: () => toast.success(t('eval.caseDeleted')),
                        onError: (e) => toast.error(apiError(e)),
                      })
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Lịch sử chạy ── */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('eval.history')}
        </p>
        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('eval.neverRun')}</p>
        ) : (
          <div className="space-y-1.5">
            {runs.map((r) => (
              <RunRow key={r.id} run={r} onOpen={() => setDetailId(r.id)} />
            ))}
          </div>
        )}
      </div>

      <CaseEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        editing={editing}
      />
      <RunDetailDialog runId={detailId} onClose={() => setDetailId(null)} />
    </div>
  )
}

function RunRow({ run, onOpen }: { run: EvalRun; onOpen: () => void }) {
  const { t } = useTranslation('aiOps')
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
    >
      <span className="text-xs text-muted-foreground">{fmtTime(run.startedAt)}</span>
      <Badge
        variant={run.status === 'done' ? 'success' : run.status === 'failed' ? 'destructive' : 'warning'}
        className="text-[10px]"
      >
        {run.status === 'running' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
        {EVAL_STATUS_LABELS[run.status]}
      </Badge>
      <span className="tabular-nums">
        {t('eval.runTotal', { count: run.total })} · <span className="text-success">{t('eval.runPassed', { count: run.passed })}</span> ·{' '}
        <span className="text-destructive">{t('eval.runFailed', { count: run.failed })}</span>
        {run.errored > 0 && <> · <span className="text-warning">{t('eval.runErrored', { count: run.errored })}</span></>}
      </span>
      {run.trigger === 'proposal' && <Badge variant="outline" className="text-[10px]">{t('eval.byProposal')}</Badge>}
      {run.model && <span className="text-xs text-muted-foreground">{run.model}</span>}
      {run.note && (
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={run.note}>
          {run.note}
        </span>
      )}
    </button>
  )
}

function CaseEditorDialog({
  open, onOpenChange, editing,
}: { open: boolean; onOpenChange: (o: boolean) => void; editing: EvalCase | null }) {
  const { t } = useTranslation('aiOps')
  const create = useCreateEvalCase()
  const update = useUpdateEvalCase()
  const botsQ = useAiBots()

  // key trên Dialog remount form theo case đang sửa — tránh bẫy "dialog giữ ảnh chụp prop cũ".
  const [name, setName] = useState(editing?.name ?? '')
  const [question, setQuestion] = useState(editing?.question ?? '')
  const [criteria, setCriteria] = useState(editing?.criteria ?? '')
  const [conversationId, setConversationId] = useState(editing?.conversationId ?? '')
  const [botId, setBotId] = useState(editing?.botId ?? NO_BOT)
  const [enabled, setEnabled] = useState(editing?.enabled ?? true)

  const pending = create.isPending || update.isPending

  const save = () => {
    const input = {
      name: name.trim(),
      question: question.trim(),
      criteria: criteria.trim(),
      conversationId: conversationId.trim() || null,
      botId: botId === NO_BOT ? null : botId,
      enabled,
    }
    const done = {
      onSuccess: () => { toast.success(t('eval.editor.saved')); onOpenChange(false) },
      onError: (e: unknown) => toast.error(apiError(e)),
    }
    if (editing) update.mutate({ id: editing.id, ...input }, done)
    else create.mutate(input, done)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} key={editing?.id ?? 'new'}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? t('eval.editor.editTitle') : t('eval.editor.addTitle')}</DialogTitle>
          <DialogDescription>
            {t('eval.editor.desc')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">{t('eval.editor.nameLabel')}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('eval.editor.namePlaceholder')} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t('eval.editor.questionLabel')}</label>
            <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t('eval.editor.questionPlaceholder')} className="mt-1 min-h-[64px]" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t('eval.editor.criteriaLabel')}</label>
            <Textarea value={criteria} onChange={(e) => setCriteria(e.target.value)} placeholder={t('eval.editor.criteriaPlaceholder')} className="mt-1 min-h-[64px]" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t('eval.editor.contextLabel')}</label>
            <Input value={conversationId ?? ''} onChange={(e) => setConversationId(e.target.value)} placeholder={t('eval.editor.contextPlaceholder')} className="mt-1" />
          </div>
          {/* eCDP luôn có Đội AI (aiBotRoutes đăng ký sẵn) nên không cần cờ. */}
          {(
            <div>
              <label className="text-xs text-muted-foreground">{t('eval.editor.botLabel')}</label>
              <Select value={botId ?? NO_BOT} onValueChange={setBotId}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_BOT}>{t('eval.editor.botDefault')}</SelectItem>
                  {(botsQ.data?.bots ?? []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={enabled} onCheckedChange={setEnabled} /> {t('eval.editor.include')}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
          <Button onClick={save} disabled={pending || !name.trim() || !question.trim() || !criteria.trim()}>
            {pending ? t('eval.editor.saving') : t('eval.editor.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RunDetailDialog({ runId, onClose }: { runId: string | null; onClose: () => void }) {
  const { t } = useTranslation('aiOps')
  const detailQ = useEvalRunDetail(runId)
  const run = detailQ.data?.run
  const results = detailQ.data?.results ?? []

  return (
    <Dialog open={!!runId} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="flex max-h-[80vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('eval.detail.title')}</DialogTitle>
          <DialogDescription>
            {run
              ? `${fmtTime(run.startedAt)} · ${t('eval.runTotal', { count: run.total })} · ${t('eval.runPassed', { count: run.passed })} · ${t('eval.runFailed', { count: run.failed })}${run.errored ? ` · ${t('eval.runErrored', { count: run.errored })}` : ''}${run.model ? ` · ${run.model}` : ''}`
              : t('eval.detail.loading')}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {detailQ.isLoading ? (
            <Loading />
          ) : run?.note && results.length === 0 ? (
            <p className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">{run.note}</p>
          ) : (
            results.map((r) => (
              <div key={r.id} className="rounded-lg border p-3 text-sm">
                <div className="mb-1 flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate font-semibold">{r.caseName}</span>
                  <Badge variant={evalVerdictVariant(r.verdict)} className="shrink-0 text-[10px]">
                    {EVAL_VERDICT_LABELS[r.verdict]}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{t('eval.asked', { text: r.question })}</p>
                {r.reply && (
                  <p className="mt-1.5 whitespace-pre-wrap rounded-md bg-muted/60 p-2 text-xs leading-relaxed">
                    {r.reply}
                  </p>
                )}
                <p className="mt-1.5 text-xs">
                  <span className="text-muted-foreground">{t('eval.detail.reasonLabel')}</span> {r.reason}
                </p>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
